import React, {
    useEffect, useContext, useState, useCallback, useMemo
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { RenewalService } from 'gw-capability-renewal';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import {
    SgSessionStorageService,
    useSgTrackPageName,
    SgCepChanges,
    ADOBE_TOPICS
} from 'saga-modules';
import { publish } from '@jutro/events';
import _ from 'lodash';
import SgCepSideMenu from 'sg-cep-side-menu';
import {
    CepConstants,
    SgVehicleChangesSummary,
    SgCoverageChangesSummary,
    SgExcessChangesTable,
    SgRenewalPremium
} from 'saga-cep-specific-components';
import { WizardPage } from 'gw-portals-wizard-react';
import { Loader } from '@jutro/components';

import { TranslatorContext } from '@jutro/locale';
import metadata from './SgRenewalQuotePage.metadata.json5';
import './SgRenewalQuotePage.messages';

function SgRenewalQuotePage(props) {
    const {
        wizardData: modelVM,
        updateWizardData: updateData,
        sgWizardPropsExt,
        goNext
    } = props;

    const {
        handleCancel
    } = sgWizardPropsExt;

    const translator = useContext(TranslatorContext);

    const jobNumber = _.get(modelVM.value, 'jobID') || _.get(modelVM.value, 'policyNumber');
    const [isLoading, setLoadingState] = useState(true);

    const viewModelService = useContext(ViewModelServiceContext);

    useSgTrackPageName(CepConstants.PAGE_NAMES.RENEWAL_PROPOSAL);

    const getValue = useCallback((value) => {
        const typelistValue = viewModelService.productMetadata.get('pc').types.getTypelist(value.typelist.name).getCode(value.code).name;
        return translator({ id: typelistValue, defaultMessage: typelistValue }).toString();
    }, [translator, viewModelService.productMetadata]);
    const [changes, setChanges] = useState(SgCepChanges.getMotorChanges(modelVM, getValue, true));

    const hasVehicleChanges = useMemo(() => {
        return SgCepChanges.hasVehicleChanges(changes);
    }, [changes]);
    const hasCoverageChanges = useMemo(() => {
        return SgCepChanges.hasCoverageChanges(changes);
    }, [changes]);
    const hasExcessChanges = useMemo(() => {
        return SgCepChanges.hasExcessChanges(changes);
    }, [changes]);

    useEffect(() => {
        if (_.get(modelVM, 'status.value.code') === 'Draft') {
            RenewalService.quote(jobNumber, modelVM.value.branchNumber)
                .then((response) => {
                    const newModelVM = viewModelService.clone(modelVM);
                    newModelVM.value = response;
                    newModelVM.renewalDetails.isAmendingRenewal.value = true;
                    updateData(newModelVM);
                    setChanges(SgCepChanges.getMotorChanges(modelVM, getValue, true));
                    publish(ADOBE_TOPICS.RENEWAL_CHANGES, {
                        changes
                    });
                    SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, newModelVM.value);
                })
                .finally(() => {
                    setLoadingState(false);
                });
        } else {
            setLoadingState(false);
        }
    }, [viewModelService]);

    const continueRenewal = useCallback(() => {
        SgSessionStorageService.removeItem(SgSessionStorageService.KEY.CEP_RENEWAL_ADDONS_PAGE_AVAILABLE);
        goNext(modelVM);
    }, [goNext, modelVM]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrideProps = {
        cepEndorsementExcessesContainer: {
            visible: hasExcessChanges
        },
        cepEndorsementCoveragesContainer: {
            visible: hasCoverageChanges
        },
        cepEndorsementVehicleContainer: {
            visible: hasVehicleChanges
        },
        vehicleChangesSummaryComponent: {
            changes,
            modelVM,
            getValue
        },
        coverageChangesSummaryComponent: {
            changes
        },
        excessChangesTableComponent: {
            changes,
            modelVM
        },
        cepRenewalPremium: {
            renewalDetails: modelVM.value.renewalDetails,
            hasRenewalChanges: true
        },
        backToMainPage: {
            onClick: (event) => {
                event.preventDefault();
                handleCancel();
            }
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            continueRenewal,

        },
        resolveComponentMap: {
            SgVehicleChangesSummary,
            SgCoverageChangesSummary,
            SgExcessChangesTable,
            SgRenewalPremium,
            SgCepSideMenu
        }
    };

    return (
        <WizardPage showPrevious={false} showNext={false}>
            {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
        </WizardPage>
    );
}

export default SgRenewalQuotePage;
